import { useTranslation } from 'next-i18next'
import React from 'react'

import {
  type FighterCard,
  FeedbackCard as SharedCard,
} from '@goodfellas/feedback-card-ui'

type CardProps = {
  fighter: FighterCard
  comparison: FighterCard | null
  transformationConfig?: string
  isLoading?: boolean
}

export default function Card(props: CardProps) {
  const { t } = useTranslation('avaliacao360')

  if (props.isLoading) {
    return (
      <div className="card animate-pulse" style={{ padding: 10 }}>
        <div
          className="card-main-element"
          style={{
            background: '#222',
            borderRadius: 30,
            padding: '3rem 0.75rem',
            textAlign: 'center',
          }}
        >
          {/* Fighter name skeleton */}
          <div className="h-10 bg-white/10 rounded-lg w-3/4 mx-auto mb-2" />

          {/* Belt skeleton */}
          <div className="flex justify-center items-center gap-2 mb-4">
            <div className="h-5 bg-white/10 rounded w-20" />
            <div className="h-4 bg-white/10 rounded w-16" />
          </div>

          {/* Radar chart skeleton - hexagonal shape */}
          <div className="flex justify-center my-6">
            <div
              className="relative"
              style={{ width: 200, height: 200 }}
            >
              <svg viewBox="0 0 200 200" className="w-full h-full">
                <polygon
                  points="100,20 170,55 170,125 100,160 30,125 30,55"
                  fill="none"
                  stroke="rgba(255,255,255,0.1)"
                  strokeWidth="2"
                />
                <polygon
                  points="100,40 150,65 150,115 100,140 50,115 50,65"
                  fill="none"
                  stroke="rgba(255,255,255,0.08)"
                  strokeWidth="1"
                />
                <polygon
                  points="100,60 130,75 130,105 100,120 70,105 70,75"
                  fill="rgba(255,255,255,0.05)"
                  stroke="rgba(255,255,255,0.1)"
                  strokeWidth="1"
                />
              </svg>
            </div>
          </div>

          {/* Game style title skeleton */}
          <div className="h-5 bg-white/10 rounded w-32 mx-auto mb-3" />

          {/* Game style bar skeleton */}
          <div className="flex justify-between items-center px-4 mb-2">
            <div className="h-4 bg-white/10 rounded w-20" />
            <div className="flex-1 mx-4 h-3 bg-white/10 rounded-full" />
            <div className="h-4 bg-white/10 rounded w-20" />
          </div>

          {/* Game skills title skeleton */}
          <div className="h-5 bg-white/10 rounded w-36 mx-auto mt-6 mb-3" />

          {/* Game skills bars skeleton */}
          <div className="space-y-2 px-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="h-3 bg-white/10 rounded w-24 flex-shrink-0" />
                <div className="flex-1 h-2 bg-white/10 rounded-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <SharedCard
      fighter={props.fighter}
      comparison={props.comparison}
      transformationConfig={props.transformationConfig}
      labels={{
        gameStyleTitle: String(t('gameStyle.title', 'Estilo de Jogo')),
        guardPlayer: String(t('gameStyle.guardPlayer', 'Guardeiro')),
        passer: String(t('gameStyle.passer', 'Passador')),
        gameSkillsTitle: String(t('gameSkills.title', 'Características')),
        gameSkillLabels: {
          guard: String(t('gameSkills.Guard', 'Guarda')),
          guardRecovery: String(
            t('gameSkills.Guard recovery', 'Reposição de Guarda'),
          ),
          guardPassing: String(
            t('gameSkills.Guard passing', 'Passagem de Guarda'),
          ),
          sweep: String(t('gameSkills.Sweep', 'Raspagem')),
          takedown: String(t('gameSkills.Takedown', 'Queda')),
          submission: String(t('gameSkills.Submission', 'Finalização')),
          submissionDefense: String(
            t('gameSkills.Submission defense', 'Defesa de Finalização'),
          ),
          positionDefense: String(
            t('gameSkills.Position defense', 'Defesa de Posição'),
          ),
        },
        chartSubjectLabels: {
          COM: String(t('cards.chart.labels.COM', 'COMPROMETIMENTO')),
          FOC: String(t('cards.chart.labels.FOC', 'FOCO')),
          AGI: String(t('cards.chart.labels.AGI', 'AGILIDADE')),
          STA: String(t('cards.chart.labels.STA', 'RESISTÊNCIA')),
          STR: String(t('cards.chart.labels.STR', 'FORÇA')),
          TEC: String(t('cards.chart.labels.TEC', 'TÉCNICA')),
        },
      }}
    />
  )
}
